// Shared helpers for the generic spreadsheet importer (Manpower shifts + Cases/OR schedules).
// Everything here runs in the browser — files are parsed locally and never uploaded anywhere.
import * as XLSX from 'xlsx';

// A single parsed spreadsheet: the header row plus each data row keyed by header text.
export interface ParsedSheet {
  headers: string[];
  rows: Record<string, string>[];
}

// Read an uploaded .xlsx / .xls / .csv file into headers + row objects. Uses `raw: false` so
// cell values arrive as the formatted text the user sees (e.g. "7:00 AM"), not Excel serials.
export async function parseSpreadsheetFile(file: File): Promise<ParsedSheet> {
  const data = await file.arrayBuffer();
  const workbook = XLSX.read(data, { type: 'array', cellDates: false });
  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) return { headers: [], rows: [] };
  const sheet = workbook.Sheets[firstSheetName];
  // header:1 → array-of-arrays so we control which row is the header.
  const matrix: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' });

  // Find the first row that has at least two non-empty cells — that's the header row.
  let headerIdx = matrix.findIndex(r => r.filter(c => String(c ?? '').trim() !== '').length >= 2);
  if (headerIdx < 0) headerIdx = 0;

  const rawHeaders = (matrix[headerIdx] || []).map(h => String(h ?? '').trim());
  // De-duplicate blank/repeated headers so the mapping dropdowns stay usable.
  const seen: Record<string, number> = {};
  const headers = rawHeaders.map((h, i) => {
    const base = h || `Column ${i + 1}`;
    if (seen[base] === undefined) { seen[base] = 0; return base; }
    seen[base] += 1;
    return `${base} (${seen[base] + 1})`;
  });

  const rows: Record<string, string>[] = [];
  for (let r = headerIdx + 1; r < matrix.length; r++) {
    const rowArr = matrix[r] || [];
    if (rowArr.every(c => String(c ?? '').trim() === '')) continue; // skip blank lines
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => { obj[h] = String(rowArr[i] ?? '').trim(); });
    rows.push(obj);
  }
  return { headers, rows };
}

// Normalize any label to lowercase alphanumeric tokens for fuzzy comparison.
export function normToken(s: string): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

// Guess which spreadsheet header best matches a field, given the field's alias hints.
export function guessHeader(headers: string[], aliases: string[]): string {
  const normHeaders = headers.map(h => ({ h, n: normToken(h) }));
  // 1) exact normalized match against any alias
  for (const a of aliases) {
    const an = normToken(a);
    const hit = normHeaders.find(x => x.n === an);
    if (hit) return hit.h;
  }
  // 2) header contains an alias (or vice versa). Require tokens of length >= 3 so a
  // trivially short header ("n", "d") can't spuriously win the substring match.
  for (const a of aliases) {
    const an = normToken(a);
    if (an.length < 3) continue;
    const hit = normHeaders.find(x => x.n.length >= 3 && (x.n.includes(an) || an.includes(x.n)));
    if (hit) return hit.h;
  }
  return '';
}

// ---- Time parsing --------------------------------------------------------

// Parse a human time-of-day into {h, m}. Accepts "7:00", "07:00", "7:00 AM", "3:30 pm",
// "0700", "700", "7 am", "7", and the same with seconds ("7:00:00 AM"), which are discarded.
// Returns null if it can't be understood.
export function parseTimeOfDay(value: string): { h: number; m: number } | null {
  if (!value) return null;
  const s = value.trim();

  // Seconds are accepted and discarded: Excel exports a time column as "7:00:00 AM" constantly, and
  // the product snaps to quarter hours anyway. Non-capturing so the meridiem stays m[3].
  let m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*([ap]\.?m\.?)?$/i);
  if (m) {
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    const ap = m[3]?.toLowerCase();
    if (ap) {
      if (ap.startsWith('p') && h < 12) h += 12;
      if (ap.startsWith('a') && h === 12) h = 0;
    }
    if (h >= 0 && h < 24 && min >= 0 && min < 60) return { h, m: min };
    return null;
  }

  m = s.match(/^(\d{1,2})\s*([ap]\.?m\.?)$/i);
  if (m) {
    let h = parseInt(m[1], 10);
    const ap = m[2].toLowerCase();
    if (ap.startsWith('p') && h < 12) h += 12;
    if (ap.startsWith('a') && h === 12) h = 0;
    if (h >= 0 && h < 24) return { h, m: 0 };
    return null;
  }

  m = s.match(/^(\d{3,4})$/); // military 0700 / 700
  if (m) {
    const digits = m[1].padStart(4, '0');
    const h = parseInt(digits.slice(0, 2), 10);
    const min = parseInt(digits.slice(2), 10);
    if (h < 24 && min < 60) return { h, m: min };
    return null;
  }

  m = s.match(/^(\d{1,2})$/); // plain hour
  if (m) {
    const h = parseInt(m[1], 10);
    if (h >= 0 && h < 24) return { h, m: 0 };
    return null;
  }

  return null;
}

// Combine a page-level date (YYYY-MM-DD) with a time cell into a UTC ISO string, interpreting
// the time in the browser's local timezone. Falls back to parsing a full datetime cell if the
// value already carries its own date. Returns null if nothing usable can be parsed.
export function toUtcFromDateAndTime(dateStr: string, timeValue: string): string | null {
  const t = parseTimeOfDay(timeValue);
  if (t) {
    const local = `${dateStr}T${String(t.h).padStart(2, '0')}:${String(t.m).padStart(2, '0')}`;
    const d = new Date(local);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
  }
  // The fallback exists for a cell that already carries its own date ("2026-03-15T10:00:00Z").
  // It must NEVER see a bare digit run: parseTimeOfDay correctly rejects "2500" as an out-of-range
  // military time, but `new Date("2500")` reads it as the YEAR 2500 and the import silently books a
  // case 474 years out. Same for "0060" (year 60 AD) and "99" (1999). A number with no separator is a
  // time of day or it is nothing -- if parseTimeOfDay could not read it, it is nothing.
  if (/^\s*\d+\s*$/.test(timeValue)) return null;
  const d2 = new Date(timeValue);
  if (!isNaN(d2.getTime())) return d2.toISOString();
  return null;
}

// ---- Name resolution -----------------------------------------------------

export interface NamedEmployee {
  employeeId: string;
  firstName: string;
  lastName: string;
  employeeType: string;
}

// Resolve a free-text name ("Smith, John" / "John Smith" / "Smith") to one employee.
// Returns the match, or an error describing why it couldn't be resolved uniquely.
export function resolveEmployeeByName(
  employees: NamedEmployee[],
  raw: string
): { ok: true; employee: NamedEmployee } | { ok: false; error: string } {
  const target = normToken(raw);
  if (!target) return { ok: false, error: 'no name given' };

  const targetTokens = target.split(' ').filter(Boolean).sort().join(' ');

  const fullMatches = employees.filter(e => {
    const first = normToken(e.firstName);
    const last = normToken(e.lastName);
    const forward = normToken(`${first} ${last}`);
    const reverse = normToken(`${last} ${first}`);
    const sortedFull = [first, last].filter(Boolean).sort().join(' ');
    return target === forward || target === reverse || targetTokens === sortedFull;
  });
  if (fullMatches.length === 1) return { ok: true, employee: fullMatches[0] };
  if (fullMatches.length > 1) return { ok: false, error: `matches ${fullMatches.length} people — add first name` };

  // Fall back to a unique last-name match.
  const lastMatches = employees.filter(e => normToken(e.lastName) === target);
  if (lastMatches.length === 1) return { ok: true, employee: lastMatches[0] };
  if (lastMatches.length > 1) return { ok: false, error: `matches ${lastMatches.length} people — add first name` };

  return { ok: false, error: 'no matching employee' };
}

export interface NamedRoom {
  operatingRoomId: string;
  name: string;
  roomNumber?: string;
  hospitalId: string;
}

// Resolve a free-text room name to one operating room, optionally scoped to a hospital name.
export function resolveRoomByName(
  rooms: NamedRoom[],
  hospitalIdByName: Map<string, string>,
  rawRoom: string,
  rawHospital: string
): { ok: true; room: NamedRoom } | { ok: false; error: string } {
  const target = normToken(rawRoom);
  if (!target) return { ok: false, error: 'no room given' };

  let pool = rooms;
  if (rawHospital && rawHospital.trim()) {
    const hid = hospitalIdByName.get(normToken(rawHospital));
    if (!hid) return { ok: false, error: `unknown hospital "${rawHospital}"` };
    pool = rooms.filter(r => r.hospitalId === hid);
  }

  const matches = pool.filter(r => {
    const byName = normToken(r.name) === target;
    const byNumber = r.roomNumber && normToken(String(r.roomNumber)) === target;
    return byName || byNumber;
  });
  if (matches.length === 1) return { ok: true, room: matches[0] };
  if (matches.length > 1) return { ok: false, error: 'ambiguous room name — add a Hospital column' };
  return { ok: false, error: 'no matching operating room' };
}
